import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Printer, FileText, ShieldCheck, Loader2, CheckCircle2,
  Clock, ClipboardList, GitCompare, Undo2, Pencil, Wand2, X, Sparkles,
} from "lucide-react";import { TopBar } from "@/components/PageShell";
import { CourtLabel } from "@/components/CourtLabel";
import { sanitizeDjenText } from "@/lib/djenText";
import { diffLines } from "@/lib/diff";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";

const ORIGIN_LABEL = {
  DJEN: "Origem DJEN",
  TEMPLATE_CONVERGE: "Origem Template IA",
  CONVERGE_AI: "Origem Convergência",
  MANUAL: "Origem Manual",
};

const fmtDateTime = (s) => s
  ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—";

const STATUS_META = {
  AGUARDANDO_REVISAO: { label: "Aguardando revisão", tone: "amber", icon: Clock },
  EM_REVISAO: { label: "Em revisão", tone: "blue", icon: Clock },
  REVISADO: { label: "Revisado", tone: "emerald", icon: CheckCircle2 },
  PROTOCOLADO: { label: "Protocolado (manualmente)", tone: "slate", icon: CheckCircle2 },
};

export default function PreparadoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [mode, setMode] = useState("edit");  // "edit" | "diff"
  const [similar, setSimilar] = useState([]);
  const [similarDismissed, setSimilarDismissed] = useState(false);

  const diff = useMemo(() => {
    if (!doc?.content_markdown_original) return null;
    return diffLines(doc.content_markdown_original, content);
  }, [doc?.content_markdown_original, content]);
  const hasDiff = diff && (diff.stats.additions + diff.stats.deletions) > 0;

  useEffect(() => {
    let stop = false;
    api.get(`/prepared-documents/${id}`)
      .then((r) => {
        if (!stop) {
          setDoc(r.data);
          setContent(r.data.content_markdown || "");
          setTitle(r.data.title || r.data.document_type || "Preparado");
        }
      })
      .catch(() => toast.error("Preparado não encontrado"))
      .finally(() => !stop && setLoading(false));
    return () => { stop = true; };
  }, [id]);

  // Detecção suave de templates similares (>= 70%) — silenciosa se falhar.
  useEffect(() => {
    if (!id) return;
    // dismissal persiste por sessão para não incomodar
    const dismissKey = `hermes:similar-dismissed:${id}`;
    if (sessionStorage.getItem(dismissKey) === "1") {
      setSimilarDismissed(true); return;
    }
    let stop = false;
    api.get(`/prepared-documents/${id}/similar-templates`, {
      params: { threshold: 0.7, limit: 3 },
    })
      .then((r) => { if (!stop) setSimilar(r.data?.items || []); })
      .catch(() => {});
    return () => { stop = true; };
  }, [id]);

  const dismissSimilar = () => {
    sessionStorage.setItem(`hermes:similar-dismissed:${id}`, "1");
    setSimilarDismissed(true);
  };

  const save = async () => {
    const trimmed = (title || "").trim();
    if (!trimmed) { toast.error("Título obrigatório"); return; }
    if (trimmed.length > 200) { toast.error("Título deve ter até 200 caracteres"); return; }
    setSaving(true);
    try {
      await api.patch(`/prepared-documents/${id}`, {
        content_markdown: content, status: "EM_REVISAO", title: trimmed,
      });
      setDirty(false); setEditingTitle(false); toast.success("Alterações salvas");
      setDoc((d) => ({ ...d, status: "EM_REVISAO", title: trimmed }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    }
    finally { setSaving(false); }
  };

  const markReviewed = async () => {
    try {
      await api.patch(`/prepared-documents/${id}`, { status: "REVISADO" });
      setDoc((d) => ({ ...d, status: "REVISADO" }));
      toast.success("Marcado como revisado");
    } catch { toast.error("Falha ao atualizar"); }
  };

  if (loading) {
    return (
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar />
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-slate-400"/></div>
      </div>
    );
  }
  if (!doc) return null;

  const status = STATUS_META[doc.status] || STATUS_META.AGUARDANDO_REVISAO;
  const StatusIcon = status.icon;
  const toneCls = {
    amber: "bg-amber-100 text-amber-900 border-amber-200",
    blue: "bg-blue-100 text-blue-900 border-blue-200",
    emerald: "bg-emerald-100 text-emerald-900 border-emerald-200",
    slate: "bg-slate-100 text-slate-800 border-slate-200",
  }[status.tone];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="preparado-detail-page">
      <TopBar />
      <div className="px-6 lg:px-10 py-8 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link to="/djen" data-testid="preparado-back"
            className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4"/> Voltar ao DJEN
          </Link>
          <Link to={`/preparados?compareFrom=${doc.id}`} data-testid="preparado-compare-cta"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-[12.5px] font-medium hover:border-slate-300">
            <GitCompare className="w-3.5 h-3.5"/> Comparar com outra peça
          </Link>
        </div>

        {/* Similaridade suave — banner com sugestão de template salvo */}
        {!similarDismissed && similar.length > 0 && (
          <div className="mb-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white p-4 flex items-start gap-3"
               data-testid="preparado-similar-banner">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}66`, color: GOLD }}>
              <Wand2 className="w-4 h-4"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-widest font-bold text-amber-900 mb-0.5">
                Template compatível detectado
              </div>
              <div className="text-sm text-slate-800">
                Esta peça tem <b>{similar[0].similarity}% de similaridade</b> com o template{" "}
                <b className="text-amber-900">"{similar[0].template.title}"</b>
                {similar.length > 1 && <span className="text-slate-500"> (+{similar.length - 1} template{similar.length > 2 ? "s" : ""})</span>}.
                Aplique-o em novas peças similares para ganhar tempo.
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {similar.map((s) => (
                  <Link key={s.template.id}
                    to={`/modelos-converge?highlight=${s.template.id}`}
                    data-testid={`preparado-similar-open-${s.template.id}`}
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded border border-amber-300 bg-white text-amber-900 text-[11.5px] font-medium hover:bg-amber-50">
                    <Sparkles className="w-3 h-3"/> {s.template.title}
                    <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 border border-amber-200 font-mono">
                      {s.similarity}%
                    </span>
                  </Link>
                ))}
                <button onClick={() => navigate(`/modelos-converge?highlight=${similar[0].template.id}`)}
                  data-testid="preparado-similar-apply"
                  className="ml-auto h-7 px-3 rounded text-white text-[11.5px] font-semibold inline-flex items-center gap-1 hover:opacity-95"
                  style={{ background: GOLD }}>
                  <Wand2 className="w-3 h-3"/> Ver e aplicar
                </button>
              </div>
            </div>
            <button onClick={dismissSimilar} data-testid="preparado-similar-dismiss"
                    aria-label="Dispensar sugestão"
                    className="w-7 h-7 rounded-md hover:bg-amber-100 inline-flex items-center justify-center text-amber-700">
              <X className="w-3.5 h-3.5"/>
            </button>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-white to-slate-50">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: GOLD }}>
              <ClipboardList className="w-3 h-3 inline mr-1"/> Preparado · {ORIGIN_LABEL[doc.origin] || `Origem ${doc.origin || "—"}`}
            </div>
            <h1 className="font-heading text-2xl mt-1 flex items-start gap-2 group" style={{ color: NAVY }} data-testid="preparado-title-wrap">
              {editingTitle ? (
                <div className="flex-1 flex items-center gap-2">
                  <input
                    autoFocus
                    value={title}
                    maxLength={200}
                    onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
                    onBlur={() => setEditingTitle(false)}
                    onKeyDown={(e) => { if (e.key === "Enter") setEditingTitle(false); if (e.key === "Escape") { setTitle(doc.title || ""); setEditingTitle(false); }}}
                    data-testid="preparado-title-input"
                    className="flex-1 min-w-0 text-2xl font-heading bg-white border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#c9953d] focus:border-[#c9953d]"
                    style={{ color: NAVY }}
                    placeholder="Título do preparado"
                  />
                  <span className={`text-[11px] font-mono ${title.length > 200 ? "text-rose-600" : "text-slate-400"}`} data-testid="preparado-title-count">
                    {title.length}/200
                  </span>
                </div>
              ) : (
                <>
                  <span className="flex-1" data-testid="preparado-title-display">
                    {sanitizeDjenText(title || doc.document_type || "Preparado")}
                  </span>
                  <button
                    onClick={() => setEditingTitle(true)}
                    data-testid="preparado-title-edit-btn"
                    aria-label="Editar título"
                    className="opacity-0 group-hover:opacity-100 transition-opacity mt-1.5 h-7 w-7 rounded-md hover:bg-slate-100 inline-flex items-center justify-center text-slate-500 hover:text-slate-900">
                    <Pencil className="w-3.5 h-3.5"/>
                  </button>
                </>
              )}
            </h1>
            <div className="mt-3 flex items-center gap-2 flex-wrap text-[12px] text-slate-700">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${toneCls}`} data-testid="preparado-status">
                <StatusIcon className="w-3 h-3"/> {status.label}
              </span>
              {doc.tribunal && <CourtLabel raw={doc.tribunal} size="xs" variant="chip"/>}
              {doc.process_number && (
                <span className="font-mono font-semibold text-slate-900">{doc.process_number}</span>
              )}
              {doc.orgao && <span className="text-slate-600">· {sanitizeDjenText(doc.orgao)}</span>}
            </div>
            <div className="mt-2 text-[11px] text-slate-500">
              Criado pelo Hermes em {fmtDateTime(doc.created_at)} · Modelo: {doc.model || "—"}
            </div>
          </div>

          <div className="px-6 py-5">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-800 flex items-start gap-2 mb-4">
              <ShieldCheck className="w-4 h-4 text-slate-500 mt-0.5 shrink-0"/>
              <span>
                Este documento foi <strong>preparado</strong> pelo Hermes e ainda <strong>não foi protocolado</strong>.
                Revise cuidadosamente antes de qualquer uso. Marcações como <code>[INDICAR: ...]</code> devem ser preenchidas.
              </span>
            </div>
            {doc.content_markdown_original && (
              <div className="mb-3 flex items-center gap-2 border-b border-slate-200">
                <TabButton active={mode === "edit"} onClick={() => setMode("edit")}
                  icon={FileText} testid="preparado-tab-edit">
                  Editor
                </TabButton>
                <TabButton active={mode === "diff"} onClick={() => setMode("diff")}
                  icon={GitCompare} testid="preparado-tab-diff"
                  badge={hasDiff ? `+${diff.stats.additions} / −${diff.stats.deletions}` : "sem alterações"}>
                  Comparar com original
                </TabButton>
                {hasDiff && (
                  <button onClick={() => { if (window.confirm("Restaurar o texto original gerado pela IA? Suas alterações serão perdidas.")) { setContent(doc.content_markdown_original); setDirty(true); toast.info("Texto original restaurado. Salve para confirmar."); } }}
                    data-testid="preparado-restore-original"
                    className="ml-auto h-8 px-3 rounded-md text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
                    <Undo2 className="w-3.5 h-3.5"/> Restaurar original
                  </button>
                )}
              </div>
            )}
            {mode === "edit" ? (
              <textarea
                value={content}
                onChange={(e) => { setContent(e.target.value); setDirty(true); }}
                data-testid="preparado-editor"
                className="w-full min-h-[520px] rounded-lg border border-slate-200 p-4 text-[14px] text-slate-900 leading-relaxed font-serif focus:outline-none focus:ring-2 focus:ring-[#c9953d] focus:border-[#c9953d]"
                placeholder="Conteúdo do documento..."
              />
            ) : (
              <DiffView diff={diff} />
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] text-slate-500">
              {dirty ? "Alterações não salvas" : "Sem alterações pendentes"}
            </div>
            <div className="flex gap-2">
              <button onClick={() => window.print()} data-testid="preparado-print"
                className="h-10 px-3 rounded-lg border border-slate-300 text-sm text-slate-800 hover:bg-white inline-flex items-center gap-2">
                <Printer className="w-4 h-4"/> Imprimir / PDF
              </button>
              <button onClick={save} disabled={saving || !dirty} data-testid="preparado-save"
                className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-semibold inline-flex items-center gap-2">
                <Save className="w-4 h-4"/> {saving ? "Salvando..." : "Salvar alterações"}
              </button>
              {doc.status !== "REVISADO" && (
                <button onClick={markReviewed} data-testid="preparado-mark-reviewed"
                  className="h-10 px-4 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-2"
                  style={{ background: GOLD }}>
                  <CheckCircle2 className="w-4 h-4"/> Marcar como revisado
                </button>
              )}
            </div>
          </div>
        </div>

        {doc.publication_id && (
          <div className="mt-4 text-[11px] text-slate-500">
            Vinculado à publicação DJEN <code className="text-slate-700">{doc.publication_id}</code>
            {doc.deadline_id && <> · Prazo <code className="text-slate-700">{doc.deadline_id}</code></>}
          </div>
        )}
      </div>
    </div>
  );
}


function TabButton({ active, onClick, icon: Icon, children, badge, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-9 px-3 border-b-2 -mb-px text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition-colors ${
        active
          ? "border-[#c9953d] text-slate-900"
          : "border-transparent text-slate-500 hover:text-slate-800"
      }`}>
      <Icon className="w-3.5 h-3.5"/> {children}
      {badge && (
        <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${
          active ? "bg-[#c9953d]/15 text-[#8f6c1a]" : "bg-slate-100 text-slate-500"
        }`}>{badge}</span>
      )}
    </button>
  );
}


function DiffView({ diff }) {
  if (!diff) return null;
  const { lines, stats } = diff;
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="preparado-diff-view">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-3 text-[11px] text-slate-700">
        <span><span className="text-emerald-700 font-semibold">+{stats.additions}</span> linhas adicionadas</span>
        <span><span className="text-rose-700 font-semibold">−{stats.deletions}</span> linhas removidas</span>
        <span className="text-slate-500">· {stats.unchanged} inalteradas</span>
        <span className="ml-auto text-[10px] text-slate-500 uppercase tracking-widest">
          original vs versão atual
        </span>
      </div>
      <div className="font-mono text-[12.5px] leading-relaxed max-h-[600px] overflow-y-auto bg-white">
        {lines.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">Sem conteúdo para comparar.</div>
        ) : lines.map((l, i) => (
          <div key={i} className={`flex ${
            l.type === "add" ? "bg-emerald-50" :
            l.type === "del" ? "bg-rose-50" : ""
          }`}>
            <span className={`w-8 shrink-0 text-center select-none border-r ${
              l.type === "add" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
              l.type === "del" ? "bg-rose-100 text-rose-800 border-rose-200" :
              "text-slate-400 border-slate-100"
            }`}>{l.type === "add" ? "+" : l.type === "del" ? "−" : " "}</span>
            <span className={`flex-1 px-3 py-0.5 whitespace-pre-wrap break-words ${
              l.type === "add" ? "text-emerald-900" :
              l.type === "del" ? "text-rose-900 line-through" :
              "text-slate-800"
            }`}>{l.text || "\u00A0"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
